import { store } from '@redux/storeConfig/store';
import { HttpWebtrack } from 'utility/http';
import { ConfigActions } from 'utility/http/http-events';
import configureInterceptors from 'utility/http/interceptors/configure-interceptors';

export const baseUrl = '/account';

interface ForgotPasswordRequest {
  userName: string;
}

interface ResetPasswordRequest {
  userId: string;
  token: string;
  password: string;
}

interface SendEmailVerificationRequest {
  userName: string;
  email: string;
}

interface EmailVerificationRequest {
  userId: string;
  token: string;
}

interface SidebarPreferencesResponse {
  userId: number;
  settingId: number;
  description: string;
  settingValue: string;
}

interface UpdateSidebarPreferencesRequest {
  collapsedCategories: number;
}

export const getForgotPasswordSerivice = async (
  request: ForgotPasswordRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);

  httpWebtrack.setConfigActions(configActions);

  return httpWebtrack.post(`${baseUrl}/ForgotPassword`, request);
};

export const resetPasswordSerivice = async (
  request: ResetPasswordRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);

  httpWebtrack.setConfigActions(configActions);

  return httpWebtrack.post(`${baseUrl}/ResetPassword`, request);
};

export const sendEmailVerificationSerivice = async (
  request: SendEmailVerificationRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);

  httpWebtrack.setConfigActions(configActions);

  return httpWebtrack.post(`${baseUrl}/SendEmailVerification`, request);
};

export const useEmailVerificationSerivice = async (
  request: EmailVerificationRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);

  httpWebtrack.setConfigActions(configActions);

  return httpWebtrack.post(`${baseUrl}/EmailVerification`, request);
};

export const getSidebarPreferencesService = async (configActions: ConfigActions) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);

  httpWebtrack.setConfigActions(configActions);

  return httpWebtrack.post(`${baseUrl}/getSidebarPreferences`, {});
};

export const updateSidebarPreferencesService = async (
  request: UpdateSidebarPreferencesRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);

  httpWebtrack.setConfigActions(configActions);

  return httpWebtrack.post(`${baseUrl}/updateSidebarPreferences`, request);
};

/* -------------------------------------------------------------------------- */
/*                         2FA (post-login, JWT normal)                       */
/* -------------------------------------------------------------------------- */
// Estos servicios consumen los endpoints de /account/2fa/* — requieren JWT verificado
// (no mfaToken). Para los endpoints pre-login (verify/resend) ver TwoFactorChallenge/services.

interface MfaEnrollVerifyRequest {
  method: string;
  code: string;
}

interface MfaDisableRequest {
  password: string;
}

export const getTwoFactorStatusService = async (configActions: ConfigActions) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.get(`${baseUrl}/2fa/status`);
};

// Marca como mostrada la invitación post-login a activar 2FA (una sola vez).
export const markTwoFactorInviteShownService = async (configActions: ConfigActions) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/2fa/invite-shown`, {});
};

export const enrollAuthenticatorService = async (
  password: string,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/2fa/enroll/authenticator`, { password });
};

export const sendEnrollmentEmailCodeService = async (
  password: string,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/2fa/enroll/email/send`, { password });
};

export const verifyEnrollmentService = async (
  request: MfaEnrollVerifyRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/2fa/enroll/verify`, request);
};

export const disableTwoFactorService = async (
  request: MfaDisableRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/2fa/disable`, request);
};

export const disableTwoFactorMethodService = async (
  method: 'Email' | 'Authenticator',
  request: MfaDisableRequest,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/2fa/disable/${method}`, request);
};

/* -------------------------------------------------------------------------- */
/*                    Passkeys (WebAuthn / FIDO2 — passwordless)              */
/* -------------------------------------------------------------------------- */
// Servicios para los endpoints de /account/passkey/*. Los de register y los de
// gestion (list/revoke/rename) requieren JWT verificado. Los de login son anonymous.
// El payload `attestation` y `assertion` viene del browser via @simplewebauthn/browser
// y se forwardea tal cual al backend (Fido2NetLib lo valida).

export const passkeyRegisterBeginService = async (configActions: ConfigActions) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/passkey/register/begin`, {});
};

export const passkeyRegisterFinishService = async (
  attestation: unknown,
  nickname: string | null,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/passkey/register/finish`, { attestation, nickname });
};

export const passkeyLoginBeginService = async (username: string | null) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  return httpWebtrack.post(`${baseUrl}/passkey/login/begin`, { username });
};

export const passkeyLoginFinishService = async (assertion: unknown) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  return httpWebtrack.post(`${baseUrl}/passkey/login/finish`, { assertion });
};

export const listPasskeysService = async (configActions: ConfigActions) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.get(`${baseUrl}/passkey`);
};

export const revokePasskeyService = async (
  passkeyId: number,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.delete(`${baseUrl}/passkey/${passkeyId}`);
};

export const renamePasskeyService = async (
  passkeyId: number,
  nickname: string,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.patch(`${baseUrl}/passkey/${passkeyId}`, { nickname } as any);
};

/* -------------------------------------------------------------------------- */
/*                    Social Login (Google / Apple — vinculacion)             */
/* -------------------------------------------------------------------------- */
// Modelo de vinculacion: el usuario Webtrack ya existe. El cliente obtiene el id_token
// del SDK del proveedor y lo manda al backend, que lo valida criptograficamente.
// - login es anonymous: si la cuenta no esta vinculada el backend responde 409 EXTERNAL_NOT_LINKED.
// - link/list/unlink requieren JWT verificado (se usan desde Settings).

export const socialLoginService = async (provider: string, idToken: string) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  return httpWebtrack.post(`${baseUrl}/social/login`, { provider, idToken });
};

export const socialLinkService = async (
  provider: string,
  idToken: string,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.post(`${baseUrl}/social/link`, { provider, idToken });
};

export const listSocialLinksService = async (configActions: ConfigActions) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  return httpWebtrack.get(`${baseUrl}/social`);
};

export const unlinkSocialService = async (
  provider: string,
  configActions: ConfigActions
) => {
  const httpWebtrack = new HttpWebtrack();
  configureInterceptors.ConfigureWebtrackInterceptor(store, httpWebtrack);
  httpWebtrack.setConfigActions(configActions);
  // POST (no DELETE) porque IIS con WebDAV intercepta DELETE → 405.
  return httpWebtrack.post(`${baseUrl}/social/${provider}/unlink`, null);
};
